/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useState, useMemo } from 'react';
import { useForm, Controller, useWatch, FieldErrors } from 'react-hook-form';
import { Box, CssBaseline, Grid, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import PageMainTitle from 'components/cmn/PageMainTitle';
import { accountFields, handlePasswordValidate } from 'libs/mindUtils';
import CustomInputLabel from 'components/cmn/CustomInputLabel';
import CustomOutlinedInput from 'components/cmn/CustomOutlinedInput';
import LoginButton from 'components/login/LoginButton';
import CustomButton from 'components/cmn/CustomButton';
import { useAppDispatch } from 'store';
import { openDialog } from 'store/dialog/dialogSlice';
import PasswordDialog from 'components/admin/account/PasswordDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import useMenu from 'hooks/useMenu';
import { useProject } from 'hooks/useProject';
import { putAdminInfo, putAdminPassword } from 'store/project/projectSlice';
import { showMessage } from 'store/message/messageSlice';

export interface AccountValues {
  name: string;
  email: string;
  mobile: string;
  password: string;
  compName: string;
  compRole: string;
  originPassword: string;
}

function AdminAccountPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleChangeMenu } = useMenu();
  const { getAdminAccountProfile } = useProject();
  const { data, mutate } = getAdminAccountProfile();
  const [showPassword, setShowPassword] = useState(false);
  const {
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    reset,
  } = useForm<AccountValues>({
    defaultValues: {
      name: data?.name ?? '-',
      email: data?.email ?? '-',
      mobile: data?.mobile ?? '-',
      password: '',
      originPassword: '',
      compName: data?.compName ?? '-',
      compRole: data?.compRole ?? '-',
    },
    mode: 'onChange',
  });
  const accountValues = useWatch({ control });

  const handleTextType = (fieldId: string, fieldType: string): string => {
    if (fieldId === 'password') {
      return showPassword ? 'text' : 'password';
    }
    return fieldType;
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleEndAdornment = (fieldId: string) => {
    if (['password'].includes(fieldId)) {
      return (
        <InputAdornment position="end">
          <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} edge="end" size="small">
            {showPassword ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        </InputAdornment>
      );
    }
    return null;
  };

  const onSubmit = async (_data: any) => {
    if (_data.originPassword && _data.password && data?.id) {
      const tmpResult = await dispatch(
        putAdminPassword({
          password: _data.originPassword as string,
          newpassword: _data.password as string,
          adminId: data?.id || '',
        }),
      ).unwrap();
      if (tmpResult.status === 200) {
        dispatch(showMessage({ message: '비밀번호가 변경되었습니다.', variant: 'success' }));
        // 성공하면 비밀번호정보 초기화
        reset({
          ..._data,
          originPassword: '',
          password: '',
        });
      }
    }

    const tmpResult = await dispatch(
      putAdminInfo({
        adminId: data?.id || '',
        mobile: _data.mobile || '',
        compName: _data.compName || '',
        compRole: _data.compRole || '',
      }),
    ).unwrap();
    if (tmpResult.status === 200) {
      mutate();
      dispatch(showMessage({ message: '회원정보가 변경되었습니다.', variant: 'success' }));
    } else {
      dispatch(showMessage({ message: tmpResult?.data?.message || '정보 변경에 실패했습니다.', variant: 'warning' }));
    }
  };

  const handleChangePassword = () => {
    dispatch(
      openDialog({
        content: <PasswordDialog values={accountValues} reset={reset} />,
      }),
    );
  };
  const handleCancel = () => {
    const previousPath = location.state?.from || '/admin/project/overview';
    const previousId = location.state?.id || 'project';
    navigate(previousPath);

    handleChangeMenu([previousId]);
  };

  const isDisabled = useMemo(
    () =>
      Object.keys(accountValues).some(field => {
        const value = accountValues[field as keyof AccountValues];
        if (field === 'password' || field === 'originPassword') {
          return value && value.length > 0 && handlePasswordValidate(value) !== true;
        }
        return value !== undefined && value.length === 0;
      }),
    [accountValues],
  );
  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Grid container>
        <Grid item xs={12} sx={{ mb: 2 }}>
          <PageMainTitle title="계정 관리" />
        </Grid>
        <form onSubmit={handleSubmit(onSubmit)} style={{ width: '450px', minWidth: '280px' }}>
          {accountFields.map(field => (
            <Grid item xs={12} mb={2.5} key={field.id}>
              <Grid item xs={12} key={field.id}>
                <Stack spacing={1.25}>
                  <Controller
                    name={field.id as keyof AccountValues}
                    control={control}
                    rules={{
                      required: field.required,
                      validate:
                        field.id === 'confirmPassword'
                          ? value => value === getValues('password') || '비밀번호가 일치하지 않습니다.'
                          : field.validate,
                    }}
                    render={({ field: { onChange, value } }) => (
                      <>
                        <CustomInputLabel htmlFor={field.id} label={field.label} />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          {field.id !== 'password' && (
                            <CustomOutlinedInput
                              id={field.id}
                              value={value}
                              onChange={e => {
                                let tmpValue = e.target.value;
                                if (field.id === 'mobile') {
                                  tmpValue = tmpValue.replace(/[^0-9]/g, '');
                                  tmpValue = tmpValue.replace(/-/g, '');
                                  tmpValue = tmpValue.substring(0, 11);
                                }
                                onChange(tmpValue);
                              }}
                              placeholder={field.placeholder}
                              fullWidth
                              error={!!(errors as FieldErrors<AccountValues>)[field.id as keyof AccountValues]}
                              message={
                                (errors as FieldErrors<AccountValues>)[field.id as keyof AccountValues]?.message || ''
                              }
                              type={handleTextType(field.id, field.type)}
                              autoComplete="off"
                              endAdornment={handleEndAdornment(field.id)}
                              disabled={field.disabled || false}
                            />
                          )}
                          {field.id === 'password' && (
                            <CustomButton
                              title="비밀번호 변경"
                              variant="contained"
                              sx={{ width: 150 }}
                              onClick={handleChangePassword}
                            />
                          )}
                        </Stack>
                      </>
                    )}
                  />
                </Stack>
              </Grid>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Stack spacing={2} direction="row" alignItems="center" justifyContent="center">
              <Grid item xs={6}>
                <LoginButton disabled={isDisabled || Object.keys(errors).length > 0} sx={{ height: 40 }}>
                  회원 정보 변경
                </LoginButton>
              </Grid>
              <Grid item xs={6}>
                <CustomButton title="취소" variant="contained" onClick={handleCancel} sx={{ mt: 1.25 }} />
              </Grid>
            </Stack>
          </Grid>
          <Grid item xs={12} sx={{ mt: 3 }}>
            <Stack direction="column">
              <Typography sx={{ typography: 's12w400', color: 'mind.grey.border2' }}>
                이메일 변경, 회원탈퇴 및 계정 삭제는 관리자에게 문의해주시기 바랍니다.
              </Typography>
              <Typography sx={{ typography: 's12w400', color: 'mind.grey.border2' }}>support@mindcode.kr</Typography>
            </Stack>
          </Grid>
        </form>
      </Grid>
    </Box>
  );
}

export default memo(AdminAccountPage);
